/* Starlog email-capture submit handler.
 *
 * Intercepts submits on any <form data-starlog-newsletter> and routes the
 * subscriber to Beehiiv via the starlog-api Cloudflare Worker, with full
 * attribution: UTM params from the URL, document.referrer, current URL,
 * a session first-touch capture, and a data-starlog-source surface tag.
 *
 * The brand-perfect custom form stays on the page; only the submit target
 * changes. Shopify customer creation is no longer triggered.
 *
 * Form contract:
 *   <form data-starlog-newsletter data-starlog-source="hero" novalidate>
 *     <p data-starlog-error hidden></p>
 *     <p data-starlog-success hidden>Thanks. ...</p>
 *     <div data-starlog-form-row>
 *       <input type="email" name="email" required>
 *       <button type="submit">Subscribe</button>
 *     </div>
 *   </form>
 *
 * API base override (for local dev):
 *   <script>window.STARLOG_API_BASE = "http://localhost:8787";</script>
 *   loaded BEFORE this script.
 *
 * Post-subscribe survey:
 *   After a successful subscribe, the existing success message stays
 *   visible AND a new tab/window is opened to the Beehiiv hosted
 *   survey at POST_SUBSCRIBE_SURVEY_URL with the subscriber's email
 *   passed as ?email=... so Beehiiv can match responses back to the
 *   right record.
 *   The new tab is OPENED SYNCHRONOUSLY on form submit (with about:blank)
 *   to retain the user gesture context that popup blockers require —
 *   we then update its location once the Worker confirms the subscribe,
 *   or close it if the subscribe failed.
 *   Override per-page with window.STARLOG_POST_SURVEY_URL, or set
 *   that to "" to disable.
 */
(function () {
  if (typeof window === "undefined") return;

  // Production endpoint. Auto-detect local dev (Shopify CLI on
  // 127.0.0.1:9292 / localhost:9292) and route to the local Worker
  // running on :8787. Manual override via window.STARLOG_API_BASE.
  var DEFAULT_API_BASE = "https://api.starlog.com";

  // Post-subscribe Beehiiv survey (in-place iframe embed).
  var POST_SUBSCRIBE_SURVEY_URL =
    typeof window.STARLOG_POST_SURVEY_URL === "string"
      ? window.STARLOG_POST_SURVEY_URL
      : "https://thelodestar.starlog.com/forms/d920ddb2-407f-4ffd-903b-00841218ede1";
  var isLocalDev =
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost";
  var API_BASE =
    window.STARLOG_API_BASE ||
    (isLocalDev ? "http://localhost:8787" : DEFAULT_API_BASE);

  // Fallback endpoint. api.starlog.com is a freshly-created custom domain and
  // can be briefly unreachable for a visitor whose resolver hasn't propagated
  // it yet (or whose network blocks it). starlog-api.starlog.workers.dev is an
  // established Cloudflare hostname that resolves everywhere, so we retry there
  // on a network-level failure ONLY (a thrown fetch = request never reached a
  // server). We never fall back on an HTTP error response, since that means the
  // primary WAS reached and re-posting could double-create a subscriber.
  var FALLBACK_API_BASE = isLocalDev
    ? null
    : "https://starlog-api.starlog.workers.dev";

  function apiFetch(path, options) {
    var primary = API_BASE.replace(/\/+$/, "") + path;
    return fetch(primary, options).catch(function (err) {
      if (FALLBACK_API_BASE && FALLBACK_API_BASE !== API_BASE) {
        return fetch(FALLBACK_API_BASE.replace(/\/+$/, "") + path, options);
      }
      throw err;
    });
  }

  var STORAGE_KEY = "starlog_attribution_v1";

  // Capture first-touch attribution once per browser. Persisted in
  // localStorage; the values are sent to Beehiiv on any future subscribe.
  function captureFirstTouchOnLoad() {
    try {
      var existing = localStorage.getItem(STORAGE_KEY);
      if (existing) return;
      var params = new URLSearchParams(window.location.search);
      var capture = {
        first_seen_at: new Date().toISOString(),
        first_landing_page:
          window.location.origin + window.location.pathname + window.location.search,
        first_referrer: document.referrer || "",
        first_utm_source: params.get("utm_source") || "",
        first_utm_medium: params.get("utm_medium") || "",
        first_utm_campaign: params.get("utm_campaign") || "",
        first_utm_content: params.get("utm_content") || "",
        first_utm_term: params.get("utm_term") || "",
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(capture));
    } catch (_) {
      /* localStorage blocked (private mode / etc.) — fail soft */
    }
  }

  function readFirstTouch() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function readCookie(name) {
    try {
      var pairs = document.cookie ? document.cookie.split("; ") : [];
      for (var i = 0; i < pairs.length; i++) {
        var idx = pairs[i].indexOf("=");
        if (idx === -1) continue;
        if (pairs[i].slice(0, idx) === name) {
          return decodeURIComponent(pairs[i].slice(idx + 1));
        }
      }
    } catch (_) {}
    return "";
  }

  function buildPayload(form) {
    var emailInput = form.querySelector('input[type="email"]');
    var email = emailInput ? emailInput.value.trim() : "";
    var source = form.getAttribute("data-starlog-source") || "";
    var params = new URLSearchParams(window.location.search);
    var firstTouch = readFirstTouch() || {};

    // Optional opt-in checkbox (magazine notify page): when checked the
    // Worker ALSO runs the normal Lodestar signup.
    var optinBox = form.querySelector('input[name="lodestar_optin"]');

    return {
      email: email,
      source: source,
      lodestar_optin: !!(optinBox && optinBox.checked),
      // Last-touch UTM (current URL takes precedence; falls back to first-touch).
      utm_source: params.get("utm_source") || firstTouch.first_utm_source || "",
      utm_medium: params.get("utm_medium") || firstTouch.first_utm_medium || "",
      utm_campaign: params.get("utm_campaign") || firstTouch.first_utm_campaign || "",
      utm_content: params.get("utm_content") || firstTouch.first_utm_content || "",
      utm_term: params.get("utm_term") || firstTouch.first_utm_term || "",
      signup_url:
        window.location.origin + window.location.pathname + window.location.search,
      referrer: document.referrer || firstTouch.first_referrer || "",
      first_landing_page: firstTouch.first_landing_page || "",
      // Ad-platform first-party cookies. Passed to the Worker so server-side
      // CAPI fires (TikTok, Meta) have usable attribution. Empty strings are
      // fine — the Worker treats missing cookies as degraded attribution,
      // not as an error.
      ttp: readCookie("_ttp"),
      fbp: readCookie("_fbp"),
      fbc: readCookie("_fbc"),
    };
  }

  function showSuccess(form) {
    var success = form.querySelector("[data-starlog-success]");
    var row = form.querySelector("[data-starlog-form-row]");
    var error = form.querySelector("[data-starlog-error]");
    if (row) row.hidden = true;
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
    if (success) success.hidden = false;
  }

  function buildSurveyURL(email) {
    if (!POST_SUBSCRIBE_SURVEY_URL) return "";
    var sep = POST_SUBSCRIBE_SURVEY_URL.indexOf("?") === -1 ? "?" : "&";
    return POST_SUBSCRIBE_SURVEY_URL + sep + "email=" + encodeURIComponent(email);
  }

  function showError(form, message) {
    var error = form.querySelector("[data-starlog-error]");
    if (error) {
      error.textContent =
        message ||
        "Something went sideways. Try again in a moment, or email hello@starlog.com.";
      error.hidden = false;
    }
  }

  function setSubmitting(form, isSubmitting) {
    var btn = form.querySelector('button[type="submit"]');
    if (btn) {
      btn.disabled = isSubmitting;
      btn.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    }
  }

  async function handleSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches("[data-starlog-newsletter]")) return;
    event.preventDefault();
    event.stopPropagation();

    var payload = buildPayload(form);
    if (!payload.email) {
      showError(form, "Please enter your email address.");
      return;
    }

    setSubmitting(form, true);

    // Open the survey tab synchronously, INSIDE the submit-event
    // gesture context, so popup blockers don't kill it. We update
    // the location after the Worker confirms the subscribe; close
    // it if the subscribe fails. Without this pre-open, a window.open
    // called after the async fetch would frequently get blocked.
    // Forms tagged data-starlog-no-survey (e.g. the magazine "notify me"
    // form) are not Lodestar signups, so the Lodestar onboarding survey
    // must not open for them.
    // ...unless the reader ticked the Lodestar opt-in box, in which case
    // they ARE a Lodestar signup and the survey applies.
    var wantsSurvey =
      !!POST_SUBSCRIBE_SURVEY_URL &&
      (!form.hasAttribute("data-starlog-no-survey") || payload.lodestar_optin === true);
    var surveyWin = null;
    if (wantsSurvey) {
      try {
        surveyWin = window.open("about:blank", "_blank");
      } catch (_) {
        surveyWin = null;
      }
    }

    var res;
    try {
      res = await apiFetch("/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
        credentials: "omit",
      });
    } catch (err) {
      setSubmitting(form, false);
      if (surveyWin) {
        try { surveyWin.close(); } catch (_) {}
      }
      showError(
        form,
        "Lost the signal for a moment. Check your connection and try again.",
      );
      return;
    }

    setSubmitting(form, false);

    var data = null;
    try {
      data = await res.json();
    } catch (_) {}

    if (res.ok && data && data.ok) {
      // Direct the pre-opened tab to the survey with the subscribed
      // email pre-filled. If the pre-open was blocked (surveyWin
      // is null), no-op gracefully — the success message in-place
      // is still shown.
      if (surveyWin) {
        try {
          surveyWin.location.href = buildSurveyURL(payload.email);
        } catch (_) {}
      }
      showSuccess(form);
      // Let host pages react to a confirmed subscribe (e.g. the Starlog 50
      // gate swaps to a "check your email" panel). Harmless elsewhere.
      try {
        form.dispatchEvent(new CustomEvent("starlog:subscribed", {
          bubbles: true,
          detail: { email: payload.email, source: payload.source || "" },
        }));
      } catch (_) {}

      // Conversion event fan-out. Fires AFTER the Worker confirms a
      // successful Beehiiv subscription — not on bare form-submit —
      // so reported conversions match actual subscribers, not abandoned
      // attempts. All four channels are best-effort with try/catch so a
      // missing global never breaks the success flow.
      var subscribeMeta = {
        source: payload.source || "",
        signup_source: payload.source || "",
      };

      // 1. GA4 — direct gtag call. Installed via Shopify Google channel.
      // Kept as a future-proofing no-op: gtag is currently sandboxed and
      // unavailable on window, so this is silently skipped. If gtag is ever
      // loaded directly into the main page, this will start firing GA4 events
      // without any other change.
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "lodestar_subscribe", {
            event_category: "engagement",
            event_label: subscribeMeta.source,
            signup_source: subscribeMeta.source,
          });
        }
      } catch (_) {}

      // 2. Shopify Customer Events sandbox — published custom event for
      // ad pixels (GA4 Measurement Protocol forwarder, Reddit, Pinterest,
      // Meta, TikTok) to subscribe to in their Custom Pixel snippets.
      // Single publish, all pixels listen.
      try {
        if (window.Shopify && window.Shopify.analytics &&
            typeof window.Shopify.analytics.publish === "function") {
          window.Shopify.analytics.publish("custom_lodestar_signup", subscribeMeta);
        }
      } catch (_) {}

      return;
    }

    // Subscribe failed — close the pre-opened tab if any.
    if (surveyWin) {
      try { surveyWin.close(); } catch (_) {}
    }

    if (res.status === 400 && data && data.error === "invalid_email") {
      showError(form, "That email doesn't look right. Mind checking it?");
      return;
    }
    if (res.status === 400 && data && data.error === "rejected_by_beehiiv") {
      showError(
        form,
        "We couldn't subscribe that address. If you think this is an error, email hello@starlog.com.",
      );
      return;
    }
    showError(form);
  }

  // Capture first-touch on every page load (no-op if already captured).
  captureFirstTouchOnLoad();

  // Delegate so we cover forms added dynamically too.
  document.addEventListener("submit", handleSubmit, true);
})();
